import React, { Dispatch, SetStateAction, useCallback, useState, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Grid, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters.tsx';
import { ufoEventOnboardingWizardProjectCreationIncludingProvisioning } from '@/components/signup/utils/ufo-events.ts';

import {
	WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME,
	ANALYTICS_PREFIX,
} from '../../common/constants';
import { useWizardConfiguration } from '../../controllers';
import { onRedirectSuccess } from '../../utils/redirect-logging';
import { IssueTypeWizardUI, type ProjectConfig } from '../../utils/types';
import { InviteStepPage } from '../invite-step/invite-step-page';
import { PageBoardColumns } from '../page-board-columns/page-board-columns';
import { PageProjectDetails } from '../page-project-details/page-project-details';
import { PageWorkTypes } from '../page-work-types/page-work-types';

const WIZARD_PAGES = [
	WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME,
	WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME,
];

const BATCH_ANALYTICS_INTERVAL = 100;

export const WelcomeModalPageSwitcher: React.FC<{
	setProjectConfig: Dispatch<SetStateAction<ProjectConfig | null>>;
	onComplete: (taskId?: string, onRedirect?: () => void) => void;
	hasFiredProjectCreate?: boolean;
	skipInviteStep?: boolean;
	isIllustrationRefresh?: boolean;
}> = ({
	setProjectConfig,
	onComplete,
	hasFiredProjectCreate,
	skipInviteStep = false,
	isIllustrationRefresh = false,
}) => {
	// HOOKS
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		wizardConfiguration,
		teamType,
		projectDetails: { template, defaultProjectName },
	} = useWizardConfiguration();
	const defaultColumns = wizardConfiguration.columns.map((defaultColumnName) =>
		formatMessage({
			id: defaultColumnName.id,
			defaultMessage: defaultColumnName.defaultMessage,
		}),
	);
	const { signupId, taskIdParam } = useSearchParameters();
	const showInviteStep = !skipInviteStep;
	const [currentPage, setCurrentPage] = useState(0);
	const [projectName, setProjectName] = useState<string | undefined>(undefined);
	const [columnNames, setColumnNames] = useState<string[]>(defaultColumns);
	const [isBoardColumnsSubmitting, setIsBoardColumnsSubmitting] = useState(false);
	const defaultIssueTypes = wizardConfiguration.issueTypes;
	const [selectedIssueTypes, setSelectedIssueTypes] = useState<IssueTypeWizardUI[]>(
		defaultIssueTypes?.filter((issue) => issue.isPreSelected) || [],
	);

	const onToggleIssueType = useCallback(
		(issueType: IssueTypeWizardUI) => {
			setSelectedIssueTypes((prevTypes) =>
				prevTypes.includes(issueType)
					? prevTypes.filter((type) => type !== issueType)
					: [...prevTypes, issueType],
			);

			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${ANALYTICS_PREFIX}WorkTypesButton`,
				eventType: 'ui',
				attributes: {
					issueTypeClicked: issueType,
					selectedIssueTypes,
					teamType,
					template,
				},
			}).fire();
		},
		[setSelectedIssueTypes, createAnalyticsEvent, selectedIssueTypes, teamType, template],
	);

	const handleSetProjectConfig = useCallback(() => {
		const hasChangedDefaultIssueTypeConfiguration = !(
			defaultIssueTypes.length === selectedIssueTypes.length &&
			defaultIssueTypes.every((defaultType) =>
				selectedIssueTypes.some((selectedType) => selectedType.id === defaultType.id),
			)
		);

		const selectedIssuesInPriorityOrder = defaultIssueTypes
			.map((defaultType) =>
				selectedIssueTypes.find((selectedType) => selectedType.id === defaultType.id),
			)
			.filter((issueType): issueType is IssueTypeWizardUI => issueType !== undefined);

		createAnalyticsEvent({
			action: 'completed',
			actionSubject: `${ANALYTICS_PREFIX}Setup`,
			eventType: 'track',
			attributes: {
				totalSelected: selectedIssueTypes.length,
				totalColumns: columnNames.length,
				defaultIssueTypes,
				selectedIssueTypes: selectedIssuesInPriorityOrder,
				hasChangedDefaultIssueTypeConfiguration,
				hasChangedDefaultProjectName: projectName !== undefined,
				teamType,
				template,
			},
		}).fire();

		ufoEventOnboardingWizardProjectCreationIncludingProvisioning.start();

		ufoEventOnboardingWizardProjectCreationIncludingProvisioning.setCustomData({ signupId });

		setProjectConfig({
			projectName: projectName === undefined ? formatMessage(defaultProjectName) : projectName,
			issueTypes: selectedIssuesInPriorityOrder.map(({ icon, avatarId }) => ({
				name: icon,
				avatarId,
			})),
			columns: columnNames,
		});
	}, [
		defaultIssueTypes,
		selectedIssueTypes,
		createAnalyticsEvent,
		columnNames,
		projectName,
		teamType,
		template,
		setProjectConfig,
		formatMessage,
		defaultProjectName,
		signupId,
	]);

	const setNextWizardPage = useCallback(() => {
		if (WIZARD_PAGES[currentPage] === WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME && showInviteStep) {
			onRedirectSuccess(template);

			// delay to allow analytics to flush
			setTimeout(() => {
				onComplete(taskIdParam ?? undefined);
			}, BATCH_ANALYTICS_INTERVAL);
		} else if (WIZARD_PAGES[currentPage] === WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME) {
			setIsBoardColumnsSubmitting(true);
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${ANALYTICS_PREFIX}FinishButton`,
				eventType: 'ui',
				attributes: {
					teamType,
					template,
				},
			}).fire();

			handleSetProjectConfig();

			if (skipInviteStep) {
				// delay to allow analytics to flush
				setTimeout(() => {
					onComplete(taskIdParam ?? undefined);
				}, BATCH_ANALYTICS_INTERVAL);
			}
		} else {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${ANALYTICS_PREFIX}NextButton`,
				eventType: 'ui',
				attributes: {
					currentPageBeforeNextClick: WIZARD_PAGES[currentPage],
					teamType,
					template,
				},
			}).fire();

			const workTypesIndex = WIZARD_PAGES.indexOf(WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME);
			if (workTypesIndex > 0 && WIZARD_PAGES[currentPage] === WIZARD_PAGES[workTypesIndex - 1]) {
				createAnalyticsEvent({
					action: 'exposed',
					actionSubject: 'feature',
					actionSubjectId: 'twcg-1659-remove-work-type-screen',
					source: 'twcg-1659-remove-work-type-screen',
					eventType: 'track',
					attributes: {
						group: 'Test',
					},
				}).fire();
				setCurrentPage(workTypesIndex + 1);
				return;
			}
			setCurrentPage(currentPage + 1);
		}
	}, [
		currentPage,
		showInviteStep,
		skipInviteStep,
		onComplete,
		taskIdParam,
		template,
		createAnalyticsEvent,
		teamType,
		handleSetProjectConfig,
	]);

	const defaultProjectNameFormatted = formatMessage(defaultProjectName);

	const handleProjectNameChange = useCallback(
		(newProjectName: string) => {
			setProjectName(newProjectName);
		},
		[setProjectName],
	);

	useEffect(() => {
		// if we have a valid taskId and have restarted the wizard, skip to invite step
		if (showInviteStep && taskIdParam && currentPage === 0) {
			setCurrentPage(WIZARD_PAGES.indexOf(WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME));
		}
	}, [showInviteStep, taskIdParam, currentPage]);

	useEffect(() => {
		if (
			showInviteStep &&
			isBoardColumnsSubmitting &&
			hasFiredProjectCreate &&
			taskIdParam !== undefined // indicates we haven't added taskId as a query param, which is only added after project create is fired
		) {
			setIsBoardColumnsSubmitting(false);
			setCurrentPage(WIZARD_PAGES.indexOf(WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME));
		}
	}, [
		isBoardColumnsSubmitting,
		setIsBoardColumnsSubmitting,
		showInviteStep,
		taskIdParam,
		hasFiredProjectCreate,
	]);

	const wizardPage = WIZARD_PAGES[currentPage] || WIZARD_PAGES[0];

	switch (wizardPage) {
		case WIZARD_PAGE_ONBOARDING_PROJECT_DETAILS_SOURCE_NAME:
		default:
			return (
				<Grid gap="space.0" xcss={gridStyles}>
					<PageProjectDetails
						onClickNext={setNextWizardPage}
						currentProjectName={projectName}
						onProjectNameChange={handleProjectNameChange}
						defaultProjectName={defaultProjectNameFormatted}
						isIllustrationRefresh={isIllustrationRefresh}
					/>
				</Grid>
			);
		case WIZARD_PAGE_ONBOARDING_WORK_TYPES_SOURCE_NAME:
			return (
				<Grid gap="space.0" xcss={gridStyles}>
					<PageWorkTypes
						defaultIssueTypes={defaultIssueTypes}
						projectName={projectName ?? defaultProjectNameFormatted}
						onClickNext={setNextWizardPage}
						selectedIssueTypes={selectedIssueTypes}
						onToggleIssueType={onToggleIssueType}
						isIllustrationRefresh={isIllustrationRefresh}
					/>
				</Grid>
			);
		case WIZARD_PAGE_ONBOARDING_BOARD_COLUMNS_SOURCE_NAME:
			return (
				<Grid gap="space.0" xcss={gridStyles}>
					<PageBoardColumns
						onClickNext={setNextWizardPage}
						projectName={projectName ?? defaultProjectNameFormatted}
						columnNames={columnNames}
						setColumnNames={setColumnNames}
						isBoardColumnsSubmitting={isBoardColumnsSubmitting}
						isFinalStep={skipInviteStep}
						isIllustrationRefresh={isIllustrationRefresh}
					/>
				</Grid>
			);
		case WIZARD_PAGE_ONBOARDING_INVITE_SOURCE_NAME:
			return (
				<Grid gap="space.0" xcss={gridStyles}>
					<InviteStepPage
						onClickNext={setNextWizardPage}
						projectName={projectName ?? defaultProjectNameFormatted}
						columnNames={columnNames}
						isIllustrationRefresh={isIllustrationRefresh}
					/>
				</Grid>
			);
	}
};

const gridStyles = xcss({
	width: '100%',
	height: '100%',
	gridTemplateColumns: '100%',
	[media.above.sm]: {
		gridTemplateColumns: '50% 50%',
	},
});
